/**
 * FilePreloadService - A sophisticated progressive file preloading system
 *
 * @see FilePreloadService.md for comprehensive documentation, usage examples,
 *      configuration profiles, best practices, and troubleshooting guide.
 *
 * Quick usage:
 * ```typescript
 * import { useFilePreload } from 'features/file';
 *
 * const { preloadFile, clearPreloads } = useFilePreload(files, currentFileId, {
 *   maxConcurrentPreloads: 3,
 *   preloadRange: 2,
 *   priorityTypes: ['image', 'pdf']
 * });
 * ```
 */
import { FileModel } from 'models';

import { FileUrlService } from 'features/exam/services/file-url-service';

interface PreloadOptions {
  /** Maximum number of files to preload simultaneously */
  maxConcurrentPreloads?: number;
  /** How many files ahead/behind to preload */
  preloadRange?: number;
  /** Delay between preload batches in milliseconds */
  batchDelay?: number;
  /** File types to prioritize for preloading */
  priorityTypes?: string[];
  /** Maximum file size to preload (in bytes) */
  maxFileSize?: number;
  /** Access token for authenticated file requests */
  accessToken?: string;
}

interface PreloadState {
  preloadedFiles: Set<number>;
  activePreloads: Map<number, HTMLLinkElement>;
  preloadQueue: Array<{ file: FileModel; priority: number }>;
  isProcessing: boolean;
}

class FilePreloadService {
  private state: PreloadState = {
    preloadedFiles: new Set(),
    activePreloads: new Map(),
    preloadQueue: [],
    isProcessing: false,
  };

  private defaultOptions: Required<PreloadOptions> = {
    maxConcurrentPreloads: 3,
    preloadRange: 2,
    batchDelay: 500,
    priorityTypes: ['image', 'pdf'],
    maxFileSize: 50 * 1024 * 1024, // 50MB
    accessToken: '',
  };

  /**
   * Preload files around the current file index with progressive loading
   * @param files - Array of files to consider for preloading
   * @param currentFileId - ID of the currently viewed file
   * @param options - Preloading configuration options
   */
  public preloadAroundFile(files: FileModel[], currentFileId: number, options: PreloadOptions = {}): void {
    const config = { ...this.defaultOptions, ...options };
    const currentIndex = files.findIndex((f) => f.id === currentFileId);

    if (currentIndex === -1) return;

    // Clear existing queue and start fresh
    this.clearQueue();

    // Build priority queue with files around current position
    const filesToPreload: Array<{ file: FileModel; priority: number }> = [];

    for (let i = 1; i <= config.preloadRange; i++) {
      // Next files (higher priority)
      const nextIndex = currentIndex + i;
      if (nextIndex < files.length) {
        const nextFile = files[nextIndex];
        if (this.shouldPreloadFile(nextFile, config)) {
          filesToPreload.push({
            file: nextFile,
            priority: 100 - i, // Higher number = higher priority
          });
        }
      }

      // Previous files (lower priority)
      const prevIndex = currentIndex - i;
      if (prevIndex >= 0) {
        const prevFile = files[prevIndex];
        if (this.shouldPreloadFile(prevFile, config)) {
          filesToPreload.push({
            file: prevFile,
            priority: 50 - i,
          });
        }
      }
    }

    // Sort by priority (highest first)
    filesToPreload.sort((a, b) => b.priority - a.priority);

    // Add to queue
    this.state.preloadQueue = filesToPreload;

    // Start processing
    this.processPreloadQueue(config);
  }

  /**
   * Preload a specific file immediately
   * @param file - File to preload
   * @param priority - Priority level (higher = more important)
   */
  public preloadFile(file: FileModel, priority: number = 50): void {
    if (!this.shouldPreloadFile(file, this.defaultOptions)) return;

    // Add to front of queue based on priority
    const insertIndex = this.state.preloadQueue.findIndex((item) => item.priority < priority);
    const queueItem = { file, priority };

    if (insertIndex === -1) {
      this.state.preloadQueue.push(queueItem);
    } else {
      this.state.preloadQueue.splice(insertIndex, 0, queueItem);
    }

    // Start processing if not already running
    if (!this.state.isProcessing) {
      this.processPreloadQueue(this.defaultOptions);
    }
  }

  /**
   * Clear all preloaded files and cancel ongoing preloads
   */
  public clearAll(): void {
    this.clearQueue();
    this.clearPreloadedFiles();
  }

  /**
   * Get preload statistics
   */
  public getStats() {
    return {
      preloadedCount: this.state.preloadedFiles.size,
      activePreloads: this.state.activePreloads.size,
      queueLength: this.state.preloadQueue.length,
      isProcessing: this.state.isProcessing,
    };
  }

  private async processPreloadQueue(config: Required<PreloadOptions>): Promise<void> {
    if (this.state.isProcessing) return;
    this.state.isProcessing = true;

    try {
      while (this.state.preloadQueue.length > 0) {
        // Process batch of files up to maxConcurrentPreloads
        const batch = this.state.preloadQueue.splice(0, config.maxConcurrentPreloads);

        // Start preloading batch concurrently
        const preloadPromises = batch.map(({ file }) => this.startFilePreload(file, config.accessToken));

        // Wait for batch to complete or fail
        await Promise.allSettled(preloadPromises);

        // Delay before next batch if there are more files
        if (this.state.preloadQueue.length > 0) {
          await this.delay(config.batchDelay);
        }
      }
    } finally {
      this.state.isProcessing = false;
    }
  }

  private async startFilePreload(file: FileModel, accessToken?: string): Promise<void> {
    if (this.state.preloadedFiles.has(file.id) || this.state.activePreloads.has(file.id)) {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = this.getFilePreloadUrl(file, accessToken);

        // Track active preload
        this.state.activePreloads.set(file.id, link);

        // Set up completion handlers
        const cleanup = () => {
          this.state.activePreloads.delete(file.id);
          this.state.preloadedFiles.add(file.id);

          // Remove link after delay to allow browser to cache
          setTimeout(() => {
            if (document.head.contains(link)) {
              document.head.removeChild(link);
            }
          }, 10000); // 10 seconds
        };

        link.onload = () => {
          cleanup();
          resolve();
        };

        link.onerror = () => {
          cleanup();
          console.warn(`Failed to preload file: ${file.fileName} (ID: ${file.id})`);
          resolve(); // Don't reject to avoid stopping the batch
        };

        // Add to DOM to start preload
        document.head.appendChild(link);

        // Timeout fallback
        setTimeout(() => {
          if (this.state.activePreloads.has(file.id)) {
            cleanup();
            resolve();
          }
        }, 30000); // 30 second timeout
      } catch (error) {
        console.error(`Error starting preload for file ${file.id}:`, error);
        this.state.activePreloads.delete(file.id);
        reject(error);
      }
    });
  }

  private shouldPreloadFile(file: FileModel, config: Required<PreloadOptions>): boolean {
    // Skip if already preloaded or being preloaded
    if (this.state.preloadedFiles.has(file.id) || this.state.activePreloads.has(file.id)) {
      return false;
    }

    // Skip if no location/URL
    if (!file.location && !file.url) {
      return false;
    }

    // Check file size limit
    if (file.fileSize && file.fileSize > config.maxFileSize) {
      return false;
    }

    // Check if file type is suitable for preloading
    const fileType = file.fileType?.toLowerCase() || '';
    const isPriorityType = config.priorityTypes.some((type) => fileType.includes(type));
    const isPreloadableType =
      isPriorityType || fileType.includes('image') || fileType.includes('pdf') || fileType.includes('text') || fileType.includes('json');

    return isPreloadableType;
  }

  private getFilePreloadUrl(file: FileModel, accessToken?: string): string {
    // If access token is provided, use the FileUrlService for authenticated URLs
    if (accessToken) {
      return FileUrlService.resolveFileUrl(file, accessToken);
    }

    // Fallback to direct URLs for unauthenticated access
    // Prefer direct file location if available
    if (file.location) {
      return file.location;
    }

    // Fall back to file URL or construct API endpoint
    if (file.url) {
      return file.url;
    }

    // Construct API endpoint as fallback
    return `/api/files/${file.id}/download`;
  }

  private clearQueue(): void {
    this.state.preloadQueue = [];
  }

  private clearPreloadedFiles(): void {
    // Cancel active preloads
    this.state.activePreloads.forEach((link, fileId) => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    });

    this.state.activePreloads.clear();
    this.state.preloadedFiles.clear();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const filePreloadService = new FilePreloadService();

// Export types for external use
export type { PreloadOptions };
